import useDebouncedState from '@hooks/Debounce/useDebounceState';
import { Box, Button, InputAdornment, Menu, MenuItem, TextField, Typography } from '@mui/material';
import { FilterListOutlined, Search } from '@mui/icons-material';
import Utils from '@utils/index';
import React from 'react';
import StreamItem from '../site/StreamItem';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { IStoreState } from '@store/index';
import SearchHeaderFilter from '@components/common/SearchHeaderFilter';
import { selectIsSuperAdminUser } from '@store/selectors';

const DisabledBackground = styled(Box)`
  opacity: 0.7;
  :hover {
    cursor: not-allowed;
  }
`

type StreamSelectorProps = {
  streams: VideoStream[];
  unavailableStreams?: VideoStream[];
  onDrag: (stream: VideoStream) => void;
  onDragEnd: () => void;
  onClick?: (stream: VideoStream) => void;
  disabled: boolean;
  containerHeight: number | string;
  backPanelHeight: number;
  titleHeight: number;
  tileLayoutHeight: number;
  title?: string;
  description?: string;
  focusedStream?: string | undefined;
}

const StreamSelector: React.FC<StreamSelectorProps> = (props) => {
  const {
    streams,
    onDrag,
    onDragEnd,
    disabled,
    containerHeight,
    backPanelHeight,
    titleHeight,
    tileLayoutHeight,
    title,
    description,
    unavailableStreams,
    onClick,
    focusedStream
  } = props;

  const isSuperAdministrator = useSelector(selectIsSuperAdminUser);

  const [searchText, setSearchText] = useDebouncedState("");
  const [filteredStreams, setFilteredStreams] = React.useState<VideoStream[]>([]);
  const [finalStreams, setFinalStreams] = React.useState<VideoStream[]>([]);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [typeFilter, setTypeFilter] = React.useState<"Active" | "Archived" | "All">("Active");
  const [searchHeight, setSearchHeight] = React.useState<number>(0)
  const rootFontSize = useSelector((state: IStoreState) => state.appView.rootFontSize)

  const measuredRef = React.useCallback((node: any | null, setState: React.Dispatch<React.SetStateAction<number>>) => {
    if (node !== null) {
      setState(node.getBoundingClientRect().height);
    }
  }, []);

  const orderStreams = React.useCallback((videoStreams: VideoStream[], unavaialbleStreams?: VideoStream[]) => {
    if (unavaialbleStreams) {
      const ordered = Utils.Array.orderAlphabetically(videoStreams, 'defaultName') as VideoStream[];
      const unavailableKvsNames = unavaialbleStreams.map(e => e.kvsName)

      return [...ordered.filter(el => unavailableKvsNames.includes(el.kvsName) === false), ...ordered.filter(el => unavailableKvsNames.includes(el.kvsName))]
    }

    return Utils.Array.orderAlphabetically(videoStreams, 'defaultName');
  }, [])

  React.useEffect(() => {
    // Filter by search text first.
    const textFilteredStreams = filteredStreams.filter(stream => Utils.Stream.getStreamName(stream).toLowerCase().includes(searchText.toLowerCase()));

    //Filter by type afterwards.
    const typeFilteredStreams = textFilteredStreams.filter(tStream => {
      if (typeFilter === "All") return true;
      if (typeFilter === "Active" && tStream.state === "ACTIVE") return true;
      if (typeFilter === "Archived" && tStream.state === "ARCHIVED") return true;
      return false;
    })

    setFinalStreams([]);
    setFinalStreams(typeFilteredStreams);
  }, [searchText, filteredStreams, typeFilter, unavailableStreams])

  function calculateMaxHeightOfGrid(): string {

    if (typeof containerHeight === "string") {
      return containerHeight
    } else {
      return `calc(${containerHeight}px - (${backPanelHeight}px + ${titleHeight}px + ${tileLayoutHeight}px + ${searchHeight}px + ${6 * rootFontSize}px))`
    }
  }

  return (
    <Box position="relative" display="grid" gridTemplateRows="min-content min-content min-content auto" paddingRight="1rem">
      {disabled === true &&
        <DisabledBackground position="absolute" width="95%" height="100%" zIndex={999} bgcolor="white" />
      }
      <Typography variant="h6" >{title || "Available cameras"}</Typography>
      <Typography variant="body2" >{description || "Drag and drop the stream thumbnail to preferred slot on the layout"}</Typography>
      <Box ref={(ref) => measuredRef(ref, setSearchHeight)} id="available-cameras-search-description" display="grid" height="unset" gridTemplateColumns="2fr 1fr">
        <TextField
          style={{ marginTop: 0, paddingTop: "1rem", paddingBottom: "1rem" }}
          variant="outlined"
          size="small"
          fullWidth
          placeholder="Search"
          onChange={(e: any) => { setSearchText(e.target.value) }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <SearchHeaderFilter
                  properties={Utils.Filters.createVideoStreamFilters(isSuperAdministrator || false)}
                  data={streams}
                  onFilteredDataChange={(data: any[]) => {
                    setFilteredStreams(data);
                  }}
                />
              </InputAdornment>
            )
          }}
          data-cy="views-stream-search-input"
        />
        <Button
          style={{ margin: "1rem" }} variant="outlined"
          startIcon={<FilterListOutlined />}
          onClick={(event) => { setAnchorEl(event.currentTarget); }}
          data-cy="views-creation-stream-filter-button"
        >
          Filter
        </Button>
        <Menu
          open={Boolean(anchorEl)}
          keepMounted
          onClose={() => { setAnchorEl(null); }}
          anchorEl={anchorEl}
        >
          <MenuItem onClick={() => { setAnchorEl(null); setTypeFilter("All"); }} data-cy={`camera-all-menu-item`} >All</MenuItem>
          <MenuItem onClick={() => { setAnchorEl(null); setTypeFilter("Active"); }} data-cy={`camera-active-menu-item`} >Active</MenuItem>
          <MenuItem onClick={() => { setAnchorEl(null); setTypeFilter("Archived"); }} data-cy={`camera-archived-menu-item`}  >Archived</MenuItem>
        </Menu>
      </Box>
      <Box id='cameras-on-present-construction-yard-description' overflow="auto" paddingRight="1rem" paddingLeft="0.25rem" paddingTop="0.25rem" paddingBottom="0.25rem" width="100%" maxHeight={calculateMaxHeightOfGrid()}>
        <Box display="grid" height="unset" gridTemplateColumns="1fr 1fr" gap="0.5rem">
          {orderStreams(finalStreams, unavailableStreams).map(stream => (
            <StreamItem
              data-cy={`div-stream-${finalStreams.indexOf(stream)}`}
              elevation={0}
              removable={false}
              draggable={true}
              height="7vw"
              key={stream.kvsName}
              stream={stream}
              onDragStart={(e) => {
                e.dataTransfer.setData("id", `${finalStreams.indexOf(stream)}`);
              }}
              onDrag={(e) => { onDrag(stream) }}
              onDragEnd={(e) => {
                onDragEnd();
              }}
              disableAnchor={true}
              disabled={Boolean(unavailableStreams?.find(el => el.kvsName === stream.kvsName))}
              onStreamClick={() => { if (onClick) { onClick(stream); } }}
              selected={focusedStream === stream.kvsName}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
}

export default StreamSelector;
